import React from 'react';
import { Redirect } from 'react-router-dom';
import { Spinner } from '@myob/myob-widgets';
import Cookies from 'js-cookie';
import { get } from 'lodash';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import { useGetProduct } from 'hooks/useGetProduct/useGetProduct';
import { useSendEventToSegment } from 'hooks/useSendEventToSegment/useSendEventToSegment';
import { AccountDetailsForm } from 'components/accountDetailsForm/AccountDetailsForm';
import { useContentViewed } from 'hooks/Analytics/useAnalytics';
import {
  ANALYTICS_ID,
  COOKIE_AJS_ANONYMOUS_ID,
  COOKIE_AJS_USER_ID,
  COOKIE_MYOB_VISITOR_ID,
  EXCLUDED_PRODUCTS,
  GTM_ANALYTICS_CONSTANTS,
  PRICING_LIST_URL,
  REGIONS
} from '../../constants';

const isProductDeprecated = (region: string, productId: string): boolean => {
  const excludedProductsByRegion = get(EXCLUDED_PRODUCTS, region);
  return excludedProductsByRegion && Object.values(excludedProductsByRegion).includes(productId);
};

export const AccountDetailPage: React.FC = () => {
  const {
    productId,
    region,
    setHasPurchaseSubmitted
  } = useSessionContext();
  setHasPurchaseSubmitted(false);

  const { isLoading: isAuth0Loading, isAuthenticated, appState } = useAuth0();
  const email = appState?.clientInformation?.email || '';
  const isNewUser = appState?.clientInformation?.isNewUser || false;

  const { isLoading: isProductLoading, isSuccess: fetchProductDataSuccess, productData } = useGetProduct();

  // This function contains React Hooks, never put it in a condition.
  trackCheckoutStarting(email, isAuth0Loading, isAuthenticated);

  if (isAuth0Loading) {
    return <Spinner/>;
  }

  if (!isAuthenticated) {
    return <Redirect to='/error' />;
  }

  if (isProductLoading) {
    return (<div data-testid='account-detail-page-loading'/>);
  }

  if (isProductDeprecated(region, productId)) {
    const redirectUrl = region === REGIONS.AUSTRALIA ? PRICING_LIST_URL.AU : PRICING_LIST_URL.NZ;
    window.location.replace(redirectUrl);
    return null;
  }

  const isProductAndRegionMatched: boolean = fetchProductDataSuccess && productData && region === productData.region;

  if (!isProductAndRegionMatched) {
    return <Redirect to='/error' />;
  }

  return (
    <AccountDetailsForm
      submitButtonText={'Continue'}
      isNewUser={isNewUser}
      emailValue={email}
    />
  );
};

export const trackCheckoutStarting = (emailValue: string, isAuth0Loading: boolean, isAuthenticated?: boolean): void => {
  // if isAuthenticated is undefined, we should regard it as false
  const enabledTracking = !isAuth0Loading && !!isAuthenticated;

  /* eslint-disable react-hooks/rules-of-hooks */
  useContentViewed({
    elementId: ANALYTICS_ID.CONTENT_VIEWED_TRIAL_FORM,
    enabled: enabledTracking
  });

  const myobVisitorId = Cookies.get(COOKIE_MYOB_VISITOR_ID);
  const anonymousId = Cookies.get(COOKIE_AJS_ANONYMOUS_ID);
  const userId = Cookies.get(COOKIE_AJS_USER_ID);
  const identifyParams = {
    userId,
    anonymousId,
    traits: {
      email: emailValue,
      myobVisitorId
    }
  };

  const trackParams = {
    event: GTM_ANALYTICS_CONSTANTS.ACCOUNT_SETUP_SEGMENT,
    userId,
    anonymousId,
    properties: {
      myobVisitorId
    }
  };

  useSendEventToSegment({ shouldSendSegmentEvent: enabledTracking, identifyParams, trackParams });
  /* eslint-enable react-hooks/rules-of-hooks */
};
